import {
  Row, Col, Card, Table, Button, Avatar, Typography,
  Tag, Tooltip, notification, Space,
} from "antd";
import {
  EditOutlined, KeyOutlined, TeamOutlined, SortAscendingOutlined,
} from "@ant-design/icons";
import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import useAppStore from "../store";
import { adminRoles, labRoleMap, rolePrettyNames } from "../constants/roles";
import { apiRoutes } from "../constants/apiRoutes";
import { filterUsers, sortUsers } from "../utils/adminUtils";

import UserSearchBar from "../components/UserSearchBar";
import EditUserModal from "../components/EditUserModal";
import ResetPasswordModal from "../components/ResetPasswordModal";
import BatchRoleModal from "../components/BatchRoleModal";

const { Title, Text } = Typography;

const ROLE_TAG_COLORS = {
  ADMIN: "red",
  NPHL_VIEWER: "blue",
  FSNRL_VIEWER: "green",
  NMRL_VIEWER: "purple",
  NIC_VIEWER: "cyan",
  GMSL_VIEWER: "magenta",
  NOBRL_VIEWER: "volcano",
  NHRL_VIEWER: "geekblue",
  NVRL_VIEWER: "lime",
  COE_VIEWER: "gold",
  DASHBOARD_USER: "default",
};

const getAuthorities = (users) =>
  [...new Set(users.map((u) => u.authority).filter(Boolean))].sort();

const stringToColor = (str = "") => {
  const colors = ["#f56a00", "#7265e6", "#ffbf00", "#00a2ae", "#1890ff", "#52c41a"];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const extractErrMsg = (e) => {
  const err = e?.response?.data?.error;
  console.error("Request error:", typeof err === "object" ? JSON.stringify(err, null, 2) : err);
  return "An error occurred. Please try again.";
};

// ─────────────────────────────────────────────────────────────────────────────

const Admin = () => {
  const user = useAppStore((s) => s.user);
  const token = useAppStore((s) => s.token);
  const users = useAppStore((s) => s.users);
  const setUsers = useAppStore((s) => s.setUsers);
  const setToken = useAppStore((s) => s.setToken);

  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isFetching, setFetching] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [filters, setFilters] = useState({
    searchText: "",
    searchFields: ["name", "email"],
    selectedLabs: [],
    selectedAuthority: null,
    unassignedOnly: false,
  });
  const [sortOrder, setSortOrder] = useState("asc");

  const [api, contextHolder] = notification.useNotification();

  const notify = (type, message, description) => {
    api[type]({ message, description, duration: 5 });
  };

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ── Fetch users ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setFetching(true);
    axios
      .get(`${process.env.REACT_APP_AUTH_API_URL}${apiRoutes.getAllUsers}`, authHeader)
      .then((res) => {
        console.log("Users fetched:", res.data);
        setUsers(res.data.data);
      })
      .catch((e) => {
        console.error("Error fetching users:", e?.response?.status, e?.response?.data || e.message);
        notify("error", "Failed to load users", "An error occurred. Please try again.");
      })
      .finally(() => setFetching(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived data ─────────────────────────────────────────────────────────────
  const authorities = useMemo(() => getAuthorities(users), [users]);

  const displayedUsers = useMemo(() => {
    const filtered = filterUsers(users, { ...filters, labRoleMap });
    return sortUsers(filtered, "name", sortOrder);
  }, [users, filters, sortOrder]);

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedRowKeys.includes(u.user_id)),
    [users, selectedRowKeys]
  );

  // ── API helpers ───────────────────────────────────────────────────────────────
  const patchUser = async (payload, user_id) => {
    const res = await axios.patch(
      `${process.env.REACT_APP_AUTH_API_URL}${apiRoutes.user}?user_id=${user_id}`,
      payload,
      authHeader
    );
    return res.data.data;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleEditFinish = async (payload, user_id) => {
    setLoading(true);
    try {
      const updatedUser = await patchUser(payload, user_id);

      if (updatedUser?.token) {
        const decoded = jwtDecode(updatedUser.token);
        if (decoded.id === user?.user_id) setToken(updatedUser.token);
      }

      setUsers(users.map((u) => (u.user_id === user_id ? { ...u, ...updatedUser } : u)));
      setEditTarget(null);
      notify("success", "User updated", `${editTarget?.first_name} was updated successfully.`);
    } catch (e) {
      notify("error", "Update failed", extractErrMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const handleResetConfirm = async (newPassword, user_id) => {
    setLoading(true);
    try {
      await patchUser({ password: newPassword }, user_id);
      setResetTarget(null);
      notify("success", "Password reset", `Password for ${resetTarget?.first_name} was reset.`);
    } catch (e) {
      notify("error", "Reset failed", extractErrMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const handleBatchRoleConfirm = async (roles) => {
    setLoading(true);
    const updates = selectedUsers.map((u) => ({ user_id: u.user_id, roles }));
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_AUTH_API_URL}${apiRoutes.getAllUsers}`,
        updates,
        authHeader
      );
      const results = res.data.results;
      const failed = results.filter((r) => r.status === "failed");
      const successIds = new Set(
        results.filter((r) => r.status === "success").map((r) => r.identifier)
      );

      setUsers(users.map((u) => (successIds.has(u.user_id) ? { ...u, roles } : u)));
      setSelectedRowKeys([]);
      setBatchModalOpen(false);

      if (failed.length > 0) {
        notify("warning", "Partial update", `${failed.length} user(s) failed to update.`);
      } else {
        notify("success", "Roles updated", `Roles updated for ${selectedUsers.length} users.`);
      }
    } catch (e) {
      notify("error", "Batch update failed", extractErrMsg(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: (
        <Space>
          Name
          <Tooltip title={`Sort ${sortOrder === "asc" ? "Z→A" : "A→Z"}`}>
            <SortAscendingOutlined
              style={{ cursor: "pointer", color: "#1890ff" }}
              onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
            />
          </Tooltip>
        </Space>
      ),
      key: "name",
      render: (_, record) => (
        <Space>
          <Avatar style={{ background: stringToColor(record.user_id) }}>
            {record.first_name?.[0]}{record.last_name?.[0]}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.first_name} {record.last_name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Authority",
      key: "authority",
      dataIndex: "authority",
      render: (val) => val ?? <Text type="secondary">-</Text>,
    },
    {
      title: "Roles",
      key: "roles",
      render: (_, record) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {record.roles
            .filter((role) => role !== "DASHBOARD_USER" || record.roles.length === 1)
            .map((role) => (
              <Tag key={role} color={ROLE_TAG_COLORS[role] ?? "default"} style={{ margin: 0 }}>
                {rolePrettyNames[role] ?? role}
              </Tag>
            ))}
        </div>
      ),
    },
    {
      title: "Date Joined",
      key: "date_created",
      render: (_, record) => new Date(record.date_created).toDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit user">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => setEditTarget(record)}
            />
          </Tooltip>
          <Tooltip title="Reset password">
            <Button
              icon={<KeyOutlined />}
              size="small"
              onClick={() => setResetTarget(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (!user?.roles.some((v) => adminRoles.includes(v))) {
    return <h1>You are not authorized to view this page.</h1>;
  }

  return (
    <>
      {contextHolder}

      <EditUserModal
        user={editTarget}
        isOpen={!!editTarget}
        isLoading={isLoading}
        onClose={() => setEditTarget(null)}
        onFinish={handleEditFinish}
      />

      <ResetPasswordModal
        user={resetTarget}
        isOpen={!!resetTarget}
        isLoading={isLoading}
        onClose={() => setResetTarget(null)}
        onConfirm={handleResetConfirm}
      />

      <BatchRoleModal
        selectedUsers={selectedUsers}
        isOpen={batchModalOpen}
        isLoading={isLoading}
        onClose={() => setBatchModalOpen(false)}
        onConfirm={handleBatchRoleConfirm}
      />

      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs={24} xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title={
                <div style={styles.cardTitle}>
                  <Title level={5} style={{ margin: 0 }}>
                    NPHL Users
                    <Text type="secondary" style={styles.userCount}>
                      {" "}({displayedUsers.length} of {users.length})
                    </Text>
                  </Title>
                  {selectedRowKeys.length > 0 && (
                    <Button
                      icon={<TeamOutlined />}
                      onClick={() => setBatchModalOpen(true)}
                      type="primary"
                      ghost
                    >
                      Change Roles ({selectedRowKeys.length})
                    </Button>
                  )}
                </div>
              }
            >
              <div style={styles.toolbar}>
                <UserSearchBar
                  onSearchChange={setFilters}
                  authorities={authorities}
                />
              </div>

              <div className="table-responsive">
                <Table
                  rowKey="user_id"
                  columns={columns}
                  dataSource={displayedUsers}
                  loading={isFetching}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  className="ant-border-space"
                  rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                    preserveSelectedRowKeys: true,
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

const styles = {
  cardTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  userCount: {
    fontSize: 14,
    fontWeight: 400,
  },
  toolbar: {
    padding: "0 24px 16px",
    display: "flex",
    justifyContent: "flex-end",
  },
};

export default Admin;